import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { ConfigService } from '../../../../shared/modules/my-common/services/config.service';
import { DataSourceService } from '../../../../shared/modules/my-common/services/datasource.service';
import { ABaseFormComponent } from '../../../../shared/modules/ui/components/abstract/a-base-form.component';
import { FORM_STATE } from '../../../../shared/modules/ui/components/abstract/a-base-model-loader.component';
import { FormHelperService } from '../../../../shared/modules/ui/services/form-helper.service';
import { LoopBackFilter, Vehicle, VehicleService } from '../../../../shared/sdk/models';
import { LoggerService } from '../../../../shared/sdk/services/custom';
import { HelperService } from '../../services/helper.service';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-vehicle-service-form',
  templateUrl: './vehicle-service-form.component.html',
  styleUrls: ['./vehicle-service-form.component.scss'],
})
export class VehicleServiceAddFormComponent extends ABaseFormComponent<VehicleService> implements OnInit {
  SUB_STATUSES = ['Universal', 'Emergency Only', 'Meals Only'];

  // $windowTitle$ = new BehaviorSubject<string>('Change Vehicle Status to “In Maintenance”');

  resetableForm = false;
  collapsibleForm = false;

  selectedVehicle: Vehicle = null;

  constructor(
    protected logger: LoggerService,
    protected fb: FormBuilder,
    protected dss: DataSourceService,
    public config: ConfigService,
    protected helper: FormHelperService<VehicleService>,
    public helperService: HelperService,
  ) {
    super(logger, fb, dss, helper);

    this.setState(FORM_STATE.COLLAPSED);
  }

  protected get ModelClass(): any {
    return VehicleService;
  }

  protected get dateFields(): string[] {
    return [];
  }

  protected get filter(): LoopBackFilter {
    return {};
  }

  async ngOnInit() {
    super.ngOnInit();
    if (!this.modelId) {
      this.helperService.autoRepairShopDso$.pipe(take(1)).subscribe((shops: any[]) => {
        const affordableShop = shops.find(shop => shop.name.includes('Affordable'));
        if (affordableShop) this.form.patchValue({ mechanicsShopId: affordableShop.id });
      });

      this.helperService.fleetMechanicsDso$.pipe(take(1)).subscribe((mechanics: any[]) => {
        if (mechanics.length === 1) this.form.patchValue({ mechanicAssignedId: mechanics[0].employee_id });
      });
    }
  }

  // public async validateAsync(): Promise<boolean> {
  //   return !!this.modelId;
  // }

  public onVehicleChange(e) {
    if (!e.event) return;
    const id = e.value;
    const name = e.event.target.innerText;
    const internalId = +name.split(' -')[0];
    this.form.patchValue({ vehicle: { id, name, internalId } });

    this.helperService.vehicleDso$.pipe(take(1)).subscribe((vehicles: Vehicle[]) => {
      this.selectedVehicle = vehicles.find(v => v.id === id);
    });
  }

  public onMechanicsShopChange(e) {
    if (!e.event) return;
    const id = e.value;
    const name = e.event.target.innerText;
    this.form.patchValue({ mechanicsShop: { id, name } });
  }

  public onFleetManagerChange(e) {
    if (!e.event) return;
    const id = e.value;
    const name = e.event.target.innerText;
    this.form.patchValue({ fleetManagerAssigned: { id, name } });
  }

  public onMechanicAssignedChange(e) {
    if (!e.event) return;
    const id = e.value;
    const name = e.event.target.innerText;
    this.form.patchValue({ mechanicAssigned: { id, name } });
  }

  protected buildForm(): void {
    this.form = this.fb.group({
      id: [null],
      miles: [null],
      vehicleId: [null, Validators.required],
      vehicle: this.fb.group({
        id: [null, Validators.required],
        name: [null, Validators.required],
        internalId: [null, Validators.required],
      }),
      mechanicsShopId: [null],
      mechanicsShop: this.fb.group({ id: [null], name: [null] }),
      dateTimeIn: [null],
      dateTimeOut: [null],
      fleetManagerAssignedId: [null],
      fleetManagerAssigned: this.fb.group({ id: [null], name: [null] }),
      mechanicAssignedId: [null],
      mechanicAssigned: this.fb.group({ id: [null], name: [null] }),
      invoices: this.fb.array([
        this.fb.group({
          amount: [null, [Validators.required, Validators.min(0)]],
          description: ['', Validators.required],
          updatedAt: [null], // Add updatedAt field
        }),
      ]),
    });
  }

  get invoices(): FormArray {
    return this.form.get('invoices') as FormArray;
  }

  protected async afterSetFormValuesAsync(model, form: FormGroup) {
    form.patchValue({
      vehicleId: model.vehicle.id,
      mechanicsShopId: (model.mechanicsShop && model.mechanicsShop.id) || null,
      fleetManagerAssignedId: (model.fleetManagerAssigned && model.fleetManagerAssigned.id) || null,
      mechanicAssignedId: (model.mechanicAssigned && model.mechanicAssigned.id) || null,
    });

    this.helperService.vehicleDso$.pipe(take(1)).subscribe((vehicles: Vehicle[]) => {
      this.selectedVehicle = vehicles.find(v => v.id === model.vehicle.id);
    });

    if (model.invoices && model.invoices.length > 0) {
      model.invoices.forEach((invoice, index) => {
        if (this.invoices.at(index)) {
          this.invoices.at(index).patchValue({
            amount: invoice.amount,
            description: invoice.description,
            updatedAt: invoice.updatedAt,
          });
        } else {
          this.invoices.push(
            this.fb.group({
              amount: [invoice.amount, [Validators.required, Validators.min(0)]],
              description: [invoice.description, Validators.required],
              updatedAt: [invoice.updatedAt],
            }),
          );
        }
      });
    }
  }

  protected async afterSubmittedAsync(data: any, id: string | number): Promise<void> {}

  protected async beforeSubmittingAsync(data: any): Promise<void> {
    await super.beforeSubmittingAsync(data);
    data.invoices = this.invoices.value;
  }

  public onInvoiceChange(index: number): void {
    const invoice = this.invoices.at(index);
    if (invoice) invoice.patchValue({ updatedAt: new Date().toISOString() });
  }

  addInvoice(): void {
    this.invoices.push(
      this.fb.group({
        amount: [null, [Validators.required, Validators.min(0)]],
        description: ['', Validators.required],
      }),
    );
  }

  removeInvoice(index: number): void {
    this.invoices.removeAt(index);
  }
}
